import type {RefObject} from 'react';
import {useState, useLayoutEffect, useRef} from 'react';

// Only read overflowY from the computed style
function isScrollable(el: Element): boolean {
  const overflowY = getComputedStyle(el).overflowY;
  return (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && el.scrollHeight > el.clientHeight;
}

function findScrollParent(el: Element | null): Element | Window {
  let curr: Element | null = el;
  while (curr) {
    if (isScrollable(curr)) return curr;
    curr = curr.parentElement;
  }
  return window;
}

/**
 * Tracks how far `elementRef` has scrolled within its nearest scrollable ancestor.
 * Returns a number between 0 and 1, where 0 = top of container and 1 = bottom of container
 * @param elementRef  a RefObject to *your* element (e.g. useRef<HTMLLIElement>())
 * @returns number
 */
export function useScrollDistance<E extends HTMLElement>(elementRef: RefObject<E | null>): number {
  const [distance, setDistance] = useState(0);
  const scrollParentRef = useRef<Element | Window>(window);

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const scrollParent = findScrollParent(el);
    scrollParentRef.current = scrollParent;

    const update = (): void => {
      const node = elementRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      let containerTop: number, containerHeight: number;

      if (scrollParent === window) {
        containerTop = 0;
        containerHeight = window.innerHeight;
      } else {
        const crect = (scrollParent as Element).getBoundingClientRect();
        containerTop = crect.top;
        containerHeight = (scrollParent as Element).clientHeight;
      }

      const elementTopInContainer = rect.top - containerTop;
      const elementHeight = rect.height;
      const maxScroll = containerHeight - elementHeight;

      const norm = maxScroll > 0 ? Math.max(0, Math.min(1, elementTopInContainer / maxScroll)) : 0;

      setDistance(norm);
    };

    if (scrollParent === window) {
      window.addEventListener('scroll', update);
    } else {
      (scrollParent as Element).addEventListener('scroll', update);
    }
    window.addEventListener('resize', update);

    update();

    return (): void => {
      if (scrollParent === window) {
        window.removeEventListener('scroll', update);
      } else {
        (scrollParent as Element).removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
    };
  }, [elementRef]);

  return distance;
}
